import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/browser';
import * as Highcharts from 'highcharts';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useGetUserConfig } from '~/api/user-config';
import { userConfig, userStore } from '~/global-state/userConfig';

export const useGetUser = () => {
  const [user, setUser] = useAtom(userConfig);

  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const { isLoading: isUserLoading, data: userData } = useGetUserConfig(isAuthenticated && !isAuthLoading);

  useEffect(() => {
    if (isUserLoading || !userData) {
      return;
    }

    const setHighchartsUserConfig = (timezone: string) => {
      Highcharts.setOptions({
        time: {
          timezone,
        },
      });
    };

    const orgName = userData?.end_consumer?.name || userData?.supplier.name || '-';
    const config = {
      commodityTypes: userData?.supplier.commodity_types ?? ['ELECTRICITY'],
      countryCode: userData?.supplier.country_code,
      featureFlags: userData?.feature_flags ?? {},
      hideProductionDevicesData: userData?.end_consumer?.hide_production_devices || false,
      hideProductionDevicesFromReports: userData?.supplier.hide_production_devices_from_reports ?? false,
      isEndConsumer: !!userData?.end_consumer,
      logoUrl: userData?.supplier.logo_url || undefined,
      matchingResolutions: userData?.supplier.matching_resolutions || [],
      orgName,
      timezone: userData?.supplier.default_timezone,
      welcomeScreenEnabled: userData?.end_consumer?.welcome_screen_enabled || false,
      dealsEnabled: userData?.supplier.deals_enabled ?? false,
      esnaEnabled: userData?.supplier.esna_enabled ?? false,
      esnaRequireLesPdfForActivation: userData?.supplier.esna_require_les_pdf_for_activation ?? false,
      ssoManagedUsers: userData?.supplier.sso_managed_users ?? false,
      portfolioLockedFrom: userData?.supplier.portfolio_locked_from ?? undefined,
      registryIntegration: userData?.supplier.registry_integration ?? {},
    };

    setUser(config);
    userStore.set(userConfig, () => config);
    setHighchartsUserConfig(userData?.supplier.default_timezone);

    Sentry.setTag('organization_name', orgName);
  }, [isUserLoading, userData, setUser]);

  // Loading until the fetched config replaces the atom's initial value, so the app never renders with the defaults
  const isLoading = user === userConfig.init;

  return { isLoading, user };
};
